import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { logger } from "@/lib/default-logger";

export type SuppressionReason = "do_not_call" | "wrong_number" | "deceased" | "operator";
export type SuppressionSource = "agent_tool" | "disposition" | "manual" | "import" | "api" | "migrated";

export interface Suppression {
	id: string;
	/** Absent on records carried over from the legacy do-not-call tag. */
	phone: string | null;
	contactId: string | null;
	reason: SuppressionReason;
	source: SuppressionSource;
	/** The person's own words, where we captured them. */
	note: string | null;
	campaignId: string | null;
	callId: string | null;
	requestedAt: string;
	createdAt: string;
	releasedAt: string | null;
	releaseNote: string | null;
	createdByUserId: string | null;
	releasedByUserId: string | null;
	/** Resolved server-side so the audit trail is readable, not a set of uuids. */
	createdByName: string | null;
	releasedByName: string | null;
	active: boolean;
}

export interface BulkSuppressionResult {
	created: number;
	duplicates: number;
	failed: { value: string; error: string }[];
}

export const REASON_LABELS: Record<SuppressionReason, string> = {
	do_not_call: "Asked not to be called",
	wrong_number: "Wrong number",
	deceased: "Deceased",
	operator: "Added by us",
};

/** How the record got here. Shown verbatim in the audit column. */
export const SOURCE_LABELS: Record<SuppressionSource, string> = {
	agent_tool: "Agent, during a call",
	disposition: "Post-call review",
	manual: "Added manually",
	import: "Bulk upload",
	api: "API",
	migrated: "Migrated from tag",
};

export const suppressionsKeys = {
	all: ["suppressions"] as const,
	lists: () => [...suppressionsKeys.all, "list"] as const,
};

function errorMessage(error: any, fallback: string): string {
	const message = error?.response?.data?.message ?? error?.response?.data?.errors;
	if (Array.isArray(message)) return message.join(" ");
	return message || fallback;
}

/**
 * This one contact's active do-not-call record, or null.
 *
 * Reads the same `crm_suppression` list the Do-not-call settings page shows, narrowed to one
 * person - so a switch on a customer record and the account-wide list can never disagree.
 */
export function useContactSuppression(contactId: string | null, enabled = true) {
	return useQuery({
		networkMode: "always",
		queryKey: [...suppressionsKeys.all, "by-contact", contactId],
		queryFn: async (): Promise<Suppression | null> => {
			const response = await apiClient.get(
				`/crm/suppressions?contactId=${contactId}&active=true&limit=1&offset=0`
			);
			return response.data?.data?.[0] ?? null;
		},
		enabled: enabled && Boolean(contactId),
		retry: false,
	});
}

export function useCreateSuppression() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: {
			phone: string;
			reason?: SuppressionReason;
			note?: string;
		}): Promise<Suppression> => {
			const response = await apiClient.post(`/crm/suppressions`, data);
			return response.data;
		},
		onSuccess: () => {
			// Suppressing terminates the contact's armed leads, so campaign views are stale too.
			queryClient.invalidateQueries({ queryKey: suppressionsKeys.all });
			queryClient.invalidateQueries({ queryKey: ["contacts"] });
			queryClient.invalidateQueries({ queryKey: ["campaigns"] });
			toast.success("Added to the do-not-call list");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "Could not add this number to the do-not-call list"));
		},
	});
}

export function useBulkCreateSuppressions() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: {
			numbers: string[];
			reason?: SuppressionReason;
			note?: string;
		}): Promise<BulkSuppressionResult> => {
			const response = await apiClient.post(`/crm/suppressions/bulk`, data);
			return response.data;
		},
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: suppressionsKeys.all });
			queryClient.invalidateQueries({ queryKey: ["contacts"] });
			queryClient.invalidateQueries({ queryKey: ["campaigns"] });

			// Report every outcome. A silent "done" after a paste where half the rows failed to
			// parse is exactly how a compliance list ends up with holes in it.
			const parts = [`${result.created} added`];
			if (result.duplicates > 0) parts.push(`${result.duplicates} already on the list`);
			if (result.failed.length > 0) parts.push(`${result.failed.length} could not be read`);
			const message = parts.join(", ");

			if (result.failed.length > 0) toast.warning(message);
			else toast.success(message);
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "Could not upload the do-not-call list"));
		},
	});
}

export function useReleaseSuppression() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, note }: { id: string; note?: string }): Promise<Suppression> => {
			const response = await apiClient.delete(`/crm/suppressions/${id}`, { data: { note } });
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: suppressionsKeys.all });
			queryClient.invalidateQueries({ queryKey: ["contacts"] });
			toast.success("Removed from the do-not-call list");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "Could not remove this number from the do-not-call list"));
		},
	});
}
